import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  TextField,
  Toolbar,
  Typography,
  Collapse,
  Divider,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { ArrowBack, Close, ExpandLess, ExpandMore, MailOutline, Phone, PhoneAndroid } from '@material-ui/icons';
import Slide from '@material-ui/core/Slide';
import { TransitionGroup } from 'react-transition-group';

import { alertRoutine } from '../../../redux/Alerts/routines';
import { getCorrespondantsRoutine, postCorrespondantRoutinePromiseCreator, putCorrespondantRoutinePromiseCreator, deleteCorrespondantRoutinePromiseCreator } from '../../../redux/Correspondants/routines';
import { makeCorrespondantsSelector } from '../../../redux/Correspondants/selectors';
import { i18n, store } from '../../../index';
import theme from '../../../utils/theme';
import { AjvValidatorContext } from '../../../utils/context/AjvContext';
import schema from './correspondantFormSchema';
import CustomSelectTextField from '../MenuTextField';

// OPTIONNEL : si vous avez déjà ces composants utilitaires, vous pouvez les réutiliser.
// Ici, j'utilise uniquement TextField/Controller pour rester autonome.

const defaultValues = {
  title: '',
  firstname: '',
  lastname: '',
  gender: 'male',
  mode_adresse: 'vous',
  category: '',
  email: '',
  phone: '',
  mobile_phone: '',
  adresse1: '',
  adresse2: '',
  postcode: '',
  city: '',
  note: '',
  archive: false,
};

function CorrespondantsDialog({ open, handleClose, handleReject, opacity, onAction, correspondant, hideBackdrop }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const themeMUI = useTheme();
  const isSmDown = useMediaQuery(themeMUI.breakpoints.down('sm'));
  const leftWidth = isSmDown ? '100%' : 360;

  const correspondantsSelector = useMemo(makeCorrespondantsSelector, []);
  const correspondants = useSelector(correspondantsSelector) || [];
  const activeCorrespondants = useMemo(
    () => correspondants.filter((c) => !c?.archive),
    [correspondants]
  );
  const archivedCorrespondants = useMemo(
    () => correspondants.filter((c) => !!c?.archive),
    [correspondants]
  );

  const { ajvResolver } = React.useContext(AjvValidatorContext);
  const {
    control,
    handleSubmit,
    reset,
    formState,
    getValues,
  } = useForm({ defaultValues, resolver: ajvResolver(schema) });

  const dirty = formState.isDirty;
  const [mode, setMode] = useState(correspondant?.id ? "edit" : correspondant ? "create" : "idle");
  const [selectedId, setSelectedId] = useState(correspondant?.id || null);
  const [openActive, setOpenActive] = useState(true);
  const [openArchived, setOpenArchived] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectedItem = correspondants.find((c) => c.id === selectedId) || null;

  const toggleActive = () => {
    const n = !openActive; setOpenActive(n); if (n) setOpenArchived(false);
  };
  const toggleArchived = () => {
    const n = !openArchived; setOpenArchived(n); if (n) setOpenActive(false);
  };

  const resetToIdle = () => { setMode('idle'); setSelectedId(null); reset(defaultValues); };
  const startCreate = () => {
    setMode('create');
    setSelectedId(null);
    reset(defaultValues);
    setOpenActive(true);
    if (!isSmDown) {
      setOpenArchived(false);
    }
  };
  const startEdit = (id) => {
    const it = correspondants.find((c) => c.id === id);
    if (!it) return;
    setMode('edit');
    setSelectedId(id);
    reset({ ...defaultValues, ...it, archive: !!it.archive });
    if (it.archive) {
      setOpenArchived(true);
      setOpenActive(false);
    } else {
      setOpenActive(true);
      if (!openArchived || isSmDown) {
        setOpenArchived(false);
      }
    }
  };

  const canClose = !dirty;
  const showList = !isSmDown || mode === 'idle';
  const showForm = !isSmDown || mode === 'create' || mode === 'edit';

  const getTitle = () => {
    if (isSmDown) {
      if (mode === 'create') return t('correspondant.Nouveau') || 'Nouveau correspondant';
      if (mode === 'edit') return t('correspondant.Modifier') || 'Modifier le correspondant';
      return t('correspondant.Liste') || 'Correspondants';
    }
    if (mode === 'create') return t('correspondant.Nouveau') || 'Nouveau correspondant';
    if (mode === 'edit') return t('correspondant.Modifier') || 'Modifier le correspondant';
    return 'Correspondants';
  };

  useEffect(() => { dispatch(getCorrespondantsRoutine.trigger()); }, [dispatch]);

  const buildPayload = (source) => {
    const base = { ...defaultValues, ...source };
    return Object.keys(defaultValues).reduce((acc, key) => {
      acc[key] = base[key];
      return acc;
    }, {});
  };

  const onSubmit = async (data) => {
    try {
      setSaving(true);
      const baseSource =
        mode === 'edit' && selectedItem
          ? { ...selectedItem, ...data, archive: !!selectedItem.archive }
          : { ...data, archive: false };
      const payload = buildPayload(baseSource);
      if (mode === 'create') {
        const res = await postCorrespondantRoutinePromiseCreator({ values: payload }, store.dispatch);
        onAction(null, { action: 'post', id: res.result });
        if (correspondant && !correspondant.id) {
          await dispatch(getCorrespondantsRoutine.trigger());
          handleClose();
          setSaving(false);
          return;
        }
      } else if (mode === 'edit' && selectedItem) {
        await putCorrespondantRoutinePromiseCreator({ values: payload, id: selectedItem.id }, store.dispatch);
        onAction(null, { action: 'put', id: selectedItem.id });
      }
      await dispatch(getCorrespondantsRoutine.trigger());
      setSaving(false);
      resetToIdle();
    } catch (e) {
      setSaving(false);
      onAction(e);
      store.dispatch(alertRoutine.show({ type: 'snack', severity: 'error', title: i18n.t('common:interface.an error occured') }));
    }
  };

  const applyArchiveStatus = async (item, nextArchive, useFormValues = false) => {
    if (!item) return;
    const source = useFormValues ? { ...item, ...getValues(), archive: nextArchive } : { ...item, archive: nextArchive };
    const payload = buildPayload(source);
    try {
      setSaving(true);
      await putCorrespondantRoutinePromiseCreator({ values: payload, id: item.id }, store.dispatch);
      dispatch(getCorrespondantsRoutine.trigger());
      if (item.id === selectedId) {
        reset(payload);
        if (nextArchive) {
          setOpenArchived(true);
          setOpenActive(false);
        } else {
          setOpenActive(true);
          if (!isSmDown) {
            setOpenArchived(false);
          }
        }
      }
    } catch (e) {
      store.dispatch(alertRoutine.show({ type: 'snack', severity: 'error', title: i18n.t('common:interface.an error occured') }));
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveToggle = async () => {
    if (!selectedItem) {
      return;
    }
    await applyArchiveStatus(selectedItem, !selectedItem.archive, true);
  };

  const handleSendEmail = () => {
    const email = getValues('email');
    if (email) window.location = `mailto:${email}`;
  };

  useEffect(() => {
    if (!correspondant) {
      return;
    }

    if (correspondant.id) {
      setMode('edit');
      setSelectedId(correspondant.id);
      reset({ ...defaultValues, ...correspondant, archive: !!correspondant.archive });
      return;
    }

    setMode('create');
    setSelectedId(null);
    reset({ ...defaultValues, ...correspondant, archive: false });
    setOpenActive(true);
    if (!isSmDown) {
      setOpenArchived(false);
    }
  }, [correspondant, reset, isSmDown]);

  return (
    <Dialog
      open={open}
      onClose={canClose ? handleClose : undefined}
      onExited={handleReject}
      fullWidth
      fullScreen={isSmDown}
      maxWidth="lg"
      PaperProps={{ style: { height: isSmDown ? '100vh' : '80vh', display: 'flex', opacity } }}
      hideBackdrop={hideBackdrop}
    >
      <Toolbar style={{ borderBottom: '1px solid rgba(0,0,0,0.12)', paddingLeft: 8, paddingRight: 8 }}>
        {isSmDown && !showList && (
          <IconButton onClick={resetToIdle}><ArrowBack /></IconButton>
        )}
        {!isSmDown && (
          <IconButton aria-label="Fermer" onClick={() => canClose && handleClose()}>
            <Close />
          </IconButton>
        )}
        <Typography variant="h6" style={{ flex: 1, marginLeft: 8 }}>{getTitle()}</Typography>
        {showList && (
          <Button variant="outlined" onClick={startCreate}>
            {t('common:interface.add', { defaultValue: 'Nouveau' })}
          </Button>
        )}
      </Toolbar>

      <DialogContent style={{ padding: 0, display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <TransitionGroup component={null}>
          {showList && (
            <Slide key="list" direction="right" in mountOnEnter unmountOnExit>
              <Box style={{ width: leftWidth, borderRight: isSmDown ? 'none' : '1px solid rgba(0,0,0,0.12)', overflow: 'auto' }}>
                <List component="nav">
                  <ListItem button onClick={toggleActive}>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          <Box marginRight={4}>{t('correspondant.actifs', { defaultValue: 'Contacts actifs' })}</Box>
                          <Badge color="primary" badgeContent={activeCorrespondants.length} showZero />
                        </Box>
                      }
                    />
                    {openActive ? <ExpandLess /> : <ExpandMore />}
                  </ListItem>
                  <Collapse in={openActive} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {activeCorrespondants.map((it) => (
                        <ListItem key={it.id} button selected={selectedId === it.id} onClick={() => startEdit(it.id)}>
                          <ListItemText primary={`${it.lastname || ''} ${it.firstname || ''}`} secondary={it.email || it.phone || ''} />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>

                  <ListItem button onClick={toggleArchived}>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          <Box marginRight={4}>{t('correspondant.archives', { defaultValue: 'Archivés' })}</Box>
                          <Badge color="primary" badgeContent={archivedCorrespondants.length} showZero />
                        </Box>
                      }
                    />
                    {openArchived ? <ExpandLess /> : <ExpandMore />}
                  </ListItem>
                  <Collapse in={openArchived} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {archivedCorrespondants.map((it) => (
                        <ListItem
                          key={it.id}
                          button
                          selected={selectedId === it.id}
                          onClick={() => startEdit(it.id)}
                        >
                          <ListItemText primaryTypographyProps={{style: {overflow: "hidden", textOverflow: "ellipsis"}}} primary={`${it.lastname || ''} ${it.firstname || ''}`} secondary={it.email || it.phone || ''} />
                          <Button
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              applyArchiveStatus(it, false);
                            }}
                            style={{flexShrink: 0}}
                          >
                            {t('common:interface.restore', { defaultValue: 'Désarchiver' })}
                          </Button>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </List>
              </Box>
            </Slide>
          )}

          {showForm && (
            <Slide key="form" direction="left" in mountOnEnter unmountOnExit>
              <Box style={{ flex: 1, padding: 16, overflow: 'auto' }}>
                {(mode === 'create' || mode === 'edit') && (
                  <Box component="form" onSubmit={handleSubmit(onSubmit)} maxWidth={760}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} >
                        {/* <Controller name="title" control={control} defaultValue="" render={({ field, fieldState: { error } }) => (
                          <TextField {...field} label={t('correspondant.titre') || 'Titre'} variant="outlined" size="small" fullWidth error={!!error} helperText={error?.message} />
                        )} /> */}
                        <CustomSelectTextField
                          type="correspondant-titre" 
                          control={control}
                          name={'title'} 
                          variant="outlined" 
                          size="small" 
                          fullWidth
                          label={t('correspondant.titre')}
                          error={Boolean(formState.errors.title)}
                          helperText={formState.errors?.title?.message}
                          actionTitle={t('correspondant.Modifier les titres')}
                          defaultItems={[
                            t('correspondant.titles.doctor'),
                            t('correspondant.titles.professor'),
                            t('correspondant.titles.mrs'),
                            t('correspondant.titles.mr'),
                          ]}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Controller name="firstname" control={control} defaultValue="" render={({ field, fieldState: { error } }) => (
                          <TextField {...field} label={t('correspondant.firstname')} variant="outlined" size="small" fullWidth error={!!error} helperText={error?.message} autoComplete="nope" />
                        )} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Controller name="lastname" control={control} defaultValue="" render={({ field, fieldState: { error } }) => (
                          <TextField {...field} label={t('correspondant.lastname')} variant="outlined" size="small" fullWidth error={!!error} helperText={error?.message} autoComplete="nope" />
                        )} />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Controller name="gender" control={control} defaultValue="male" render={({ field, fieldState: { error } }) => (
                          <TextField {...field} select label={t('correspondant.gender')} variant="outlined" size="small" fullWidth error={!!error} helperText={error?.message}>
                            <MenuItem value="male">{t('correspondant.male')}</MenuItem>
                            <MenuItem value="female">{t('correspondant.female')}</MenuItem>
                          </TextField>
                        )} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Controller name="mode_adresse" control={control} defaultValue="vous" render={({ field, fieldState: { error } }) => (
                          <TextField {...field} select label={t('correspondant.mode_adresse')} variant="outlined" size="small" fullWidth error={!!error} helperText={error?.message}>
                            <MenuItem value="vous">{t('correspondant.vous')}</MenuItem>
                            <MenuItem value="tu">{t('correspondant.tu')}</MenuItem>
                          </TextField>
                        )} />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <CustomSelectTextField
                          type="correspondant-category" 
                          control={control}
                          name={'category'} 
                          variant="outlined" 
                          size="small" 
                          fullWidth
                          label={t('correspondant.category')}
                          error={Boolean(formState.errors.category)}
                          helperText={formState.errors?.category?.message}
                          actionTitle={t('correspondant.Modifier les catégories')}
                          defaultItems={[
                            t('correspondant.categories.dentist'),
                            t('correspondant.categories.oral_surgeon'),
                            t('correspondant.categories.maxillofacial_surgeon'),
                            t('correspondant.categories.ent'),
                            t('correspondant.categories.physiotherapist'),
                            t('correspondant.categories.speech_therapist'),
                          ]}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Controller name="email" control={control} defaultValue="" render={({ field, fieldState: { error } }) => (
                          <TextField {...field} label={t('correspondant.email')} variant="outlined" size="small" fullWidth error={!!error} helperText={error?.message} autoComplete="nope" InputProps={{ endAdornment: (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={handleSendEmail}><MailOutline color="action" /></IconButton>
                            </InputAdornment>
                          )}} />
                        )} />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Controller name="phone" control={control} defaultValue="" render={({ field, fieldState: { error } }) => (
                          <TextField {...field} label={t('correspondant.phone')} variant="outlined" size="small" fullWidth error={!!error} helperText={error?.message} autoComplete="nope" InputProps={{ endAdornment: (
                            <InputAdornment position="end"><Phone color="action" /></InputAdornment>
                          )}} />
                        )} />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Controller name="mobile_phone" control={control} defaultValue="" render={({ field, fieldState: { error } }) => (
                          <TextField {...field} label={t('correspondant.mobile_phone')} variant="outlined" size="small" fullWidth error={!!error} helperText={error?.message} autoComplete="nope" InputProps={{ endAdornment: (
                            <InputAdornment position="end"><PhoneAndroid color="action" /></InputAdornment>
                          )}} />
                        )} />
                      </Grid>

                      <Grid item xs={12}>
                        <Controller name="adresse1" control={control} defaultValue="" render={({ field, fieldState: { error } }) => (
                          <TextField {...field} label={t('correspondant.adresse1')} variant="outlined" size="small" fullWidth error={!!error} helperText={error?.message} autoComplete="nope" />
                        )} />
                      </Grid>
                      <Grid item xs={12}>
                        <Controller name="adresse2" control={control} defaultValue="" render={({ field, fieldState: { error } }) => (
                          <TextField {...field} label={t('correspondant.adresse2')} variant="outlined" size="small" fullWidth error={!!error} helperText={error?.message} autoComplete="nope" />
                        )} />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Controller name="postcode" control={control} defaultValue="" render={({ field, fieldState: { error } }) => (
                          <TextField {...field} label={t('correspondant.postcode')} variant="outlined" size="small" fullWidth error={!!error} helperText={error?.message} autoComplete="nope" />
                        )} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Controller name="city" control={control} defaultValue="" render={({ field, fieldState: { error } }) => (
                          <TextField {...field} label={t('correspondant.city')} variant="outlined" size="small" fullWidth error={!!error} helperText={error?.message} autoComplete="nope" />
                        )} />
                      </Grid>

                      <Grid item xs={12}>
                        <Controller name="note" control={control} defaultValue="" render={({ field, fieldState: { error } }) => (
                          <TextField {...field} label={t('correspondant.note')} variant="outlined" size="small" fullWidth multiline minRows={3} error={!!error} helperText={error?.message} autoComplete="nope" />
                        )} />
                      </Grid>
                    </Grid>
                  </Box>
                )}
                {mode === 'idle' && !isSmDown && (
                  <Box style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="h6" color="textSecondary">Sélectionnez un correspondant dans la liste ou cliquez sur « Nouveau »</Typography>
                  </Box>
                )}
              </Box>
            </Slide>
          )}
        </TransitionGroup>
      </DialogContent>

      <Divider />
      <DialogActions style={{ padding: '12px 16px', display: 'flex', alignItems: 'center' }}>
        {mode === 'edit' && selectedItem ? (
          <Button
            onClick={handleArchiveToggle}
            style={{ color: selectedItem.archive ? '#388e3c' : theme().palette.error.main }}
            disabled={saving}
          >
            {selectedItem.archive
              ? t('common:interface.restore', { defaultValue: 'Désarchiver' })
              : t('common:interface.archive', { defaultValue: 'Archiver' })}
          </Button>
        ) : (
          <span />
        )}
        <Box style={{ flexGrow: 1 }} />
        {mode === 'idle' ? (
          <Button onClick={handleClose}>Fermer</Button>
        ) : (
          <>
            <Button onClick={resetToIdle}>Annuler</Button>
            <Button color="primary" onClick={handleSubmit(onSubmit)} disabled={saving || (mode === 'edit' && !dirty)}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export const correspondantsSplitDialogActionCreator = (
  correspondant,
  parentAlertId,
  cb = () => {}
) => {
  return alertRoutine.show({
    type: "full_dialog",
    parent: parentAlertId,
    //neverHide: true,
    content: (props) => (
      <CorrespondantsDialog
        {...props}
        correspondant={correspondant}
        onAction={cb}
      />
    ),
  });
};

// Si vous avez encore besoin d’une suppression définitive, utilisez l’action ci-dessous.

export const deleteCorrespondantFormActionCreator = (correspondant, parentAlertId, cb = () => {}) => (
  alertRoutine.show({
    type: 'dialog',
    maxWidth: 'xs',
    parent: parentAlertId,
    title: i18n.t('correspondant.delete correspondant'),
    content: i18n.t('correspondant.delete correspondant detail'),
    action: async (values) => {
      
      try {
        const res = await deleteCorrespondantRoutinePromiseCreator({
          id: correspondant.id,
        }, store.dispatch)   
        
        cb(null, {action: 'delete', id: res.result})
        return true
      }
      catch (e) {
        //afficher l'erreur
        cb(e)
        store.dispatch(errorActionCreator())
        return false
      }
      finally {
      }         
    }
  })
)

const errorActionCreator = () => (
  alertRoutine.show({
    type: 'snack',
    title: i18n.t('common:interface.an error occured'),
    severity: 'error'
  })
)
